import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Users, MagnifyingGlass } from "@phosphor-icons/react";
import { isSupervisorOrAbove, isAdminOrAbove } from "@/lib/permissions";
import { useAuth } from "@/context/AuthContext";

/**
 * Public roster — everyone in the system with their roles and cert.
 *
 * Supervisors and above get an extra "Last Login" column showing the most
 * recent login stamp formatted as MM/DD/YY HH:MM in 24-hour time (BSVAC
 * runs on military time everywhere).
 */
function fmtLastLogin(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${mm}/${dd}/${yy} · ${hh}:${mi}`;
}

const ROLE_META = {
  volunteer:          { label: "Volunteer",          badge: "bg-emerald-700 text-white" },
  supervisor:         { label: "Supervisor",         badge: "bg-blue-600 text-white" },
  admin:              { label: "Admin",              badge: "bg-slate-900 text-white" },
  staff_chief:        { label: "Staff Chief",        badge: "bg-fuchsia-700 text-white" },
  commanding_officer: { label: "Commanding Officer", badge: "bg-rose-700 text-white" },
  super_admin:        { label: "Super Admin",        badge: "bg-indigo-900 text-white" },
  logistics:          { label: "Logistics",          badge: "bg-amber-600 text-white" },
};

function firstNameKey(u) {
  // Sort by first name (given name) — the token before the first space.
  // Falls back to the full name for single-word display names.
  return String(u.name || "").trim().split(/\s+/)[0].toLowerCase();
}

export default function Roster() {
  const { user: currentUser } = useAuth();
  const canSeeLastLogin = isSupervisorOrAbove(currentUser);
  const canOpenProfiles = isSupervisorOrAbove(currentUser) || isAdminOrAbove(currentUser);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.get("/users/")
      .then((r) => { if (!cancelled) setUsers(r.data || []); })
      .catch(() => { if (!cancelled) setUsers([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sorted = [...users].sort((a, b) => firstNameKey(a).localeCompare(firstNameKey(b)));
    if (!needle) return sorted;
    return sorted.filter((u) => {
      const hay = [u.name, u.email, u.username, u.role, ...(u.additional_roles || []), (u.certifications || []).join(" ")]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q]);

  return (
    <AppLayout
      title="Member Roster"
      subtitle={loading ? "Loading roster…" : `${filtered.length} member${filtered.length === 1 ? "" : "s"} · sorted by first name`}
      actions={
        <div className="relative">
          <MagnifyingGlass size={14} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, role, cert…"
            className="rounded-sm pl-7 w-64"
            data-testid="roster-search"
          />
        </div>
      }
    >
      <div className="surface-grid" data-testid="roster-grid">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center gap-2">
          <Users size={16} weight="bold" className="text-slate-500" />
          <div className="font-display font-bold">All Members</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white border-b border-slate-200">
            <tr className="text-left tab-tag text-slate-500">
              <th className="px-4 py-2">Member</th>
              <th className="px-4 py-2">Roles</th>
              <th className="px-4 py-2">Certifications</th>
              <th className="px-4 py-2">Contact</th>
              {canSeeLastLogin && <th className="px-4 py-2">Last Login</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={canSeeLastLogin ? 5 : 4} className="px-4 py-6 text-slate-500 text-center">No members match.</td></tr>
            ) : filtered.map((u) => {
              const roles = [u.role, ...(u.additional_roles || [])].filter(Boolean);
              return (
                <tr key={u.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`roster-row-${u.id}`}>
                  <td className="px-4 py-2">
                    {canOpenProfiles || u.id === currentUser?.id ? (
                      <Link
                        to={`/members/${u.id}`}
                        className="font-medium text-slate-900 hover:text-blue-700 hover:underline"
                        data-testid={`roster-open-${u.id}`}
                      >
                        {u.name}
                      </Link>
                    ) : (
                      <span className="font-medium text-slate-900" data-testid={`roster-name-${u.id}`}>{u.name}</span>
                    )}
                    <div className="text-[11px] text-slate-500 mono">{u.username ? `@${u.username}` : u.email}</div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {roles.map((r) => (
                        <Badge key={r} className={`rounded-sm text-[10px] ${ROLE_META[r]?.badge || "bg-slate-600 text-white"}`}>
                          {ROLE_META[r]?.label || r.replace(/_/g, " ")}
                        </Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex flex-wrap gap-1">
                      {(u.certifications || [u.certification].filter(Boolean)).map((c) => (
                        <Badge key={c} className="rounded-sm bg-slate-700 text-white text-[10px]">{c}</Badge>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2 mono text-[11px] text-slate-600">
                    <div>{u.email}</div>
                    {u.phone && <div className="text-slate-400">{u.phone}</div>}
                  </td>
                  {canSeeLastLogin && (
                    <td className="px-4 py-2 mono text-xs text-slate-700" data-testid={`roster-last-login-${u.id}`}>
                      {fmtLastLogin(u.last_login)}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
